import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, TrendingUp, Shield, RefreshCw, Building, DollarSign, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvestmentGuide = () => {
  const { language } = useLanguage();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${API}/guides/investment`);
      setGuides(response.data);
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const iconMap = {
    'Why Invest in DRC': <TrendingUp className="h-8 w-8 text-secondary" />,
    'Investment Guarantees': <Shield className="h-8 w-8 text-secondary" />,
    'Business Reforms': <RefreshCw className="h-8 w-8 text-secondary" />,
    'Key Investment Sectors': <Building className="h-8 w-8 text-secondary" />,
    'Currency & Exchange': <DollarSign className="h-8 w-8 text-secondary" />,
    'Tax System for Companies': <FileText className="h-8 w-8 text-secondary" />
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="investment-guide-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1633960719106-894ffd8b34d2?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary/95"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {language === 'fr' ? 'Guide d\'Investissement' : 'Investment Guide'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {language === 'fr' 
              ? 'Tout ce que vous devez savoir pour investir en RDC'
              : 'Everything you need to know about investing in DRC'}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="investment-guides">
        <div className="max-w-6xl mx-auto space-y-8">
          {guides.map((guide, idx) => (
            <motion.div
              key={guide.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="bg-card border border-border/40 p-8 hover:border-primary/50 transition-colors"
              data-testid={`guide-${guide.id}`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {iconMap[guide.section] || <FileText className="h-8 w-8 text-secondary" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl font-bold mb-4 text-foreground">
                    {guide.section}
                  </h3>
                  <h4 className="text-lg font-semibold mb-3 text-primary">
                    {guide.title}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {guide.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted" data-testid="key-sectors">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl font-bold mb-12 text-center text-foreground"
          >
            {language === 'fr' ? 'Secteurs Prioritaires' : 'Priority Sectors'}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: language === 'fr' ? 'Mines' : 'Mining', icon: '⛏️' },
              { name: language === 'fr' ? 'Agriculture' : 'Agriculture', icon: '🌾' },
              { name: language === 'fr' ? 'Énergie' : 'Energy', icon: '⚡' },
              { name: language === 'fr' ? 'Infrastructure' : 'Infrastructure', icon: '🏗️' },
              { name: language === 'fr' ? 'Fabrication' : 'Manufacturing', icon: '🏭' },
              { name: language === 'fr' ? 'Technologie' : 'Technology', icon: '💻' }
            ].map((sector, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-card p-6 border border-border hover:border-secondary transition-colors text-center"
              >
                <div className="text-4xl mb-3">{sector.icon}</div>
                <h3 className="font-serif text-lg font-semibold text-foreground">{sector.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentGuide;